// Shared scroll tracker + scroll lock. Runs one rAF loop and one scroll
// listener for the whole page so multiple consumers (e.g. webgl backgrounds)
// don't each bind their own.
//
// Scroll lock is done with `overflow: hidden` on the root scroller (<html>),
// NOT `position: fixed` on <body>. Fixed-body pulls the page out of flow, which
// zeroes window.scrollY and collapses scrollHeight for as long as the modal is
// open — every scroll-position / page-height consumer (GSAP ScrollTrigger
// scrubs & pins, navbar theme triggers, the lifetime sticky push) then rewinds
// to the top and snaps back on close. Plain `overflow: hidden` freezes the page
// exactly where it is: scrollY and scrollHeight are untouched, so nothing reacts.
//
// `scrollbar-gutter: stable` (see _main.scss) keeps the layout from shifting
// when the scrollbar disappears; the padding fallback below covers browsers
// without that support. A non-passive touchmove guard keeps iOS Safari from
// scrolling the page behind the modal while still allowing scroll inside a
// scrollable panel.
const supportsScrollbarGutter =
  typeof CSS !== "undefined" && CSS.supports?.("scrollbar-gutter", "stable");

function isScrollableInside(node) {
  while (node && node !== document.body && node.nodeType === 1) {
    const style = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

class Scroll {
  constructor() {
    this.y = window.scrollY;
    this.lastY = this.y;
    this.delta = 0;
    this.locked = false;

    this.lockCount = 0;

    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.raf();
  }

  onScroll = () => {
    if (this.locked) return;
    this.y = window.scrollY;
  };

  raf = () => {
    this.delta = this.locked ? 0 : this.y - this.lastY;
    this.lastY = this.y;
    requestAnimationFrame(this.raf);
  };

  // iOS Safari ignores `overflow: hidden` on <html> for touch scrolling, so
  // swallow touchmoves that aren't inside a scrollable panel of the open modal.
  onTouchMove = (e) => {
    if (e.touches.length > 1) return; // pinch-zoom
    if (!isScrollableInside(e.target)) e.preventDefault();
  };

  lockScroll() {
    if (this.lockCount === 0) {
      const html = document.documentElement;

      if (!supportsScrollbarGutter) {
        const sbw = window.innerWidth - html.clientWidth;
        if (sbw > 0) html.style.paddingRight = `${sbw}px`;
      }
      html.style.overflow = "hidden";
      window.addEventListener("touchmove", this.onTouchMove, { passive: false });

      this.locked = true;
      this.delta = 0;
    }
    this.lockCount++;
  }

  unlockScroll() {
    if (this.lockCount === 0) return;

    this.lockCount--;
    if (this.lockCount === 0) {
      const html = document.documentElement;

      html.style.overflow = "";
      html.style.paddingRight = "";
      window.removeEventListener("touchmove", this.onTouchMove, { passive: false });

      // Page never moved, so just resync the tracker to the current position.
      this.y = window.scrollY;
      this.lastY = this.y;
      this.delta = 0;
      this.locked = false;
    }
  }
}

const scroll = new Scroll();

export default scroll;
export const lockScroll = () => scroll.lockScroll();
export const unlockScroll = () => scroll.unlockScroll();
